import type {
    AccountModelAchFiltersDto,
    AchFilterAccountMasterSettingsModelDto,
    AchFilterAuditEventModelDto,
    AchFilterModelDto,
    AchFiltersSearchRequestModelDto,
    AchFilterUserModelDto,
    SaveAchFiltersRequestModelDto,
} from '@treasury/api/channel';
import { JhHttpResponseType } from '@jkhy/frontend-utils/http';
import { TmHttpClient } from '@treasury/core/http';
import { AchFilter } from './ach-filter.entity';

export interface AchFiltersSearchResult {
    filters: AchFilter[];
    totalCount: number;
}

export interface AchFilterAccount {
    accountUniqueId: string;
    accountNumber: string;
    accountNickname?: string;
    accountType?: string;
    claims?: string[];
}

export interface AchFilterAuditEvent {
    actionDate: string;
    actionUsername: string;
    action: string;
}

export interface AchFilterApprover {
    name: string;
    userUniqueId: string;
    alias?: string;
}

export interface AchFilterSaveRequest {
    achFilterUniqueId?: string;
    accountUniqueId?: string;
    accountNumber: string;
    accountType: string;
    achCompanyId: string;
    achCompanyName?: string;
    allowType: string;
    transactionType: string;
    filterRuleName?: string;
    secCode?: string;
    fromAmount?: number;
    toAmount?: number;
    expirationDate?: string;
    remarks?: string;
    routingNumber?: string;
}

export interface AchFilterDownloadRequest {
    accountUniqueIds?: string[];
    achCompanyId?: string;
    achCompanyName?: string;
    secCodes?: string[];
    amountStart?: number;
    amountEnd?: number;
    statuses?: string[];
    transactionTypes?: string[];
    downloadType?: string;
}

export class AchFiltersService {
    public static async search(
        request: AchFiltersSearchRequestModelDto = {}
    ): Promise<AchFiltersSearchResult> {
        const http = await TmHttpClient.getInstance();
        const response = await http.request('achFilters/search', {
            method: 'POST',
            body: request,
        });

        const { achFilters = [], paging } = response as {
            achFilters: AchFilterModelDto[];
            paging: { totalCount: number };
        };

        return {
            filters: achFilters.map(dto => new AchFilter(dto)),
            totalCount: paging?.totalCount ?? achFilters.length,
        };
    }

    public static async save(
        request: AchFilterSaveRequest
    ): Promise<{ achFilterUniqueId?: string }> {
        const http = await TmHttpClient.getInstance();
        const body: SaveAchFiltersRequestModelDto = {
            achFilterUniqueId: request.achFilterUniqueId,
            accountUniqueId: request.accountUniqueId,
            accountNumber: request.accountNumber,
            accountType: request.accountType,
            achCompanyId: request.achCompanyId,
            achCompanyName: request.achCompanyName,
            allowType: request.allowType,
            transactionType: request.transactionType,
            filterRuleName: request.filterRuleName,
            secCode: request.secCode,
            fromAmount: request.fromAmount,
            toAmount: request.toAmount,
            expirationDate: request.expirationDate,
            remarks: request.remarks,
            routingNumber: request.routingNumber,
        };
        return http.request('achFilters/save', {
            method: 'POST',
            body,
        });
    }

    public static async approve(achFilterUniqueIds: string[]): Promise<unknown> {
        const http = await TmHttpClient.getInstance();
        return http.request('achFilters/approve', {
            method: 'POST',
            body: { AchFilterUniqueIds: achFilterUniqueIds },
        });
    }

    public static async reject(achFilterUniqueIds: string[]): Promise<unknown> {
        const http = await TmHttpClient.getInstance();
        return http.request('achFilters/reject', {
            method: 'POST',
            body: { AchFilterIds: achFilterUniqueIds },
        });
    }

    public static async deleteFilter(achFilterUniqueIds: string[]): Promise<unknown> {
        const http = await TmHttpClient.getInstance();
        return http.request('achFilters/delete', {
            method: 'POST',
            body: { AchFilterUniqueIds: achFilterUniqueIds },
        });
    }

    public static async getAccounts(): Promise<AchFilterAccount[]> {
        const http = await TmHttpClient.getInstance();
        const accounts = (await http.request('achFilters/all/accounts', {
            method: 'GET',
            maxAgeInSeconds: 30,
        })) as AccountModelAchFiltersDto[];
        return (accounts ?? []).map(a => ({
            accountUniqueId: a.accountUniqueId ?? '',
            accountNumber: a.accountNumber ?? '',
            accountNickname: a.accountNickname,
            accountType: a.accountType,
            claims: a.claims as string[] | undefined,
        }));
    }

    public static async getAccountsCreate(): Promise<AchFilterAccount[]> {
        const http = await TmHttpClient.getInstance();
        const accounts = (await http.request('achFilters/create/accounts', {
            method: 'GET',
            maxAgeInSeconds: 30,
        })) as AccountModelAchFiltersDto[];
        return (accounts ?? []).map(a => ({
            accountUniqueId: a.accountUniqueId ?? '',
            accountNumber: a.accountNumber ?? '',
            accountNickname: a.accountNickname,
            accountType: a.accountType,
            claims: a.claims as string[] | undefined,
        }));
    }

    public static async getAccountMasterSettings(
        accountUniqueId: string
    ): Promise<AchFilterAccountMasterSettingsModelDto> {
        const http = await TmHttpClient.getInstance();
        return http.request(`achFilters/account/${accountUniqueId}/masterSettings`, {
            method: 'GET',
            maxAgeInSeconds: 300,
        });
    }

    public static async getAuditLog(achFilterUniqueId: string): Promise<AchFilterAuditEvent[]> {
        const http = await TmHttpClient.getInstance();
        const response = (await http.request('achFilters/auditHistory', {
            method: 'POST',
            body: { AchFilterUniqueIds: [achFilterUniqueId] },
        })) as Array<{ achFilterUniqueId?: string; auditEvents?: AchFilterAuditEventModelDto[] }>;

        const history = response?.[0];
        return (history?.auditEvents ?? []).map(e => ({
            actionDate: e.actionDate ?? '',
            actionUsername: e.actionUsername ?? '',
            action: e.action ?? '',
        }));
    }

    public static async findEligibleApprovers(
        achFilterUniqueId: string
    ): Promise<AchFilterApprover[]> {
        const http = await TmHttpClient.getInstance();
        const approvers = (await http.request(`achFilters/eligibleApprovers/${achFilterUniqueId}`, {
            method: 'GET',
            maxAgeInSeconds: 5000,
        })) as AchFilterUserModelDto[];
        return (approvers ?? []).map(a => ({
            name: a.name ?? '',
            userUniqueId: a.userUniqueId ?? '',
            alias: a.alias,
        }));
    }

    public static async getSecCodes(): Promise<string[]> {
        const http = await TmHttpClient.getInstance();
        const codes = (await http.request('secCodes', {
            method: 'GET',
            maxAgeInSeconds: 600,
        })) as string[];
        return codes ?? [];
    }

    public static async download(request: AchFilterDownloadRequest): Promise<ArrayBuffer> {
        const http = await TmHttpClient.getInstance();
        const body = {
            accountUniqueIds: request.accountUniqueIds,
            achCompanyId: request.achCompanyId,
            achCompanyName: request.achCompanyName,
            secCodes: request.secCodes,
            amountStart: request.amountStart,
            amountEnd: request.amountEnd,
            statuses: request.statuses,
            transactionTypes: request.transactionTypes,
            downloadType: request.downloadType ?? 'CSV',
        };
        return http.request('AchFilterReportDownload', {
            method: 'POST',
            body,
            responseType: JhHttpResponseType.ArrayBuffer,
        });
    }
}
